'use client';

import type {
	ApplianceId,
	DailyRecommendationPlan,
	MonthlyOutlookItem,
	RecommendationMonthlyOutlook,
} from '@slims/shared/calculations';
import { fmtNum } from '@slims/shared/calculations';
import { useMemo, useState } from 'react';
import { Grid, Text } from '../../atoms';
import {
	AiBox,
	CardBadge,
	DataTable,
	type DataTableColumn,
	Footnote,
	IncentiveRow,
	LabeledSelect,
	PanelCard,
	StatTile,
	StatusBlock,
} from '../../molecules';

/** Ora civile come etichetta: `13` → `13:00`. */
const hourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

/** Finestra oraria leggibile. `toHour` è esclusiva: 13→15 si legge `13:00–15:00`. */
const windowLabel = (fromHour: number, toHour: number) =>
	`${hourLabel(fromHour)}–${hourLabel(toHour)}`;

const APPLIANCE_ICON: Record<ApplianceId, string> = {
	lavatrice: '🧺',
	lavastoviglie: '🍽️',
	ev: '🚗',
	pompa_calore: '🌡️',
	storage: '🔋',
};

const formatDay = (date: string) =>
	new Intl.DateTimeFormat('it-IT', { weekday: 'long', day: '2-digit', month: 'long' }).format(
		new Date(`${date}T12:00:00`),
	);

/** Perché la CER non ha ancora raccomandazioni da mostrare. */
export type DailyRecommendationsReason = 'no-withdrawal-pods' | 'no-production-forecast';

const REASON_MESSAGE: Record<DailyRecommendationsReason, string> = {
	'no-withdrawal-pods':
		'Nessun POD di prelievo censito: senza le curve di consumo dei soci non c’è un carico da spostare.',
	'no-production-forecast':
		'Nessun impianto a catasto con potenza dichiarata: senza curva di produzione non esiste un surplus da intercettare.',
};

export interface DailyRecommendationsMember {
	memberId: string;
	memberName: string;
	/** POD di prelievo del socio che compongono la previsione. */
	pods: number;
	/** Quota del surplus della CER assegnata al socio nelle ore di sole, 0-1. */
	surplusShare: number;
	today: DailyRecommendationPlan;
	monthly: RecommendationMonthlyOutlook;
}

export interface DailyRecommendationsPanelProps {
	/** Almeno un socio ha un'azione da fare oggi o nel mese. */
	available: boolean;
	/** Giorno civile del piano, 'YYYY-MM-DD'. */
	date: string | null;
	/** Giorni di previsione su cui è costruita la proiezione mensile. */
	horizonDays: number;
	members: DailyRecommendationsMember[];
	/** Somme sulla CER: energia condivisa in più oggi e nel mese, e soci coinvolti. */
	totals: { todayKwh: number; monthlyKwh: number; members: number };
	loading?: boolean;
	reason?: DailyRecommendationsReason | null;
	/**
	 * Per un riquadro stretto (la colonna laterale di MySlims): riquadri numerici
	 * impilati e niente tabella mensile, che il riepilogo in fondo già somma.
	 */
	compact?: boolean;
}

/**
 * Raccomandazioni giornaliere per socio (INB-42).
 *
 * Per ogni carico differibile: a che ora farlo partire e quanta energia della
 * CER intercetta, cioè quanta energia condivisa in più ne viene.
 *
 * La view non calcola nulla: riceve i piani già risolti e possiede solo il socio
 * selezionato.
 */
function DailyRecommendationsPanelView({
	available,
	date,
	horizonDays,
	members,
	totals,
	loading,
	reason,
	compact = false,
}: DailyRecommendationsPanelProps) {
	const [selectedId, setSelectedId] = useState<string | null>(null);

	// Il socio scelto, o il primo della lista. La lista si riordina a ogni giro di
	// previsione: tenere l'id e non l'indice evita di seguire la riga sbagliata.
	const selected = useMemo(
		() => members.find((m) => m.memberId === selectedId) ?? members[0] ?? null,
		[members, selectedId],
	);

	const monthlyColumns: DataTableColumn<MonthlyOutlookItem>[] = [
		{
			key: 'appliance',
			header: 'Carico',
			render: (row) => `${APPLIANCE_ICON[row.appliance]} ${row.label}`,
		},
		{
			key: 'days',
			header: 'Giorni utili',
			align: 'right',
			variant: 'muted',
			render: (row) => `${row.days}/${horizonDays}`,
		},
		{
			key: 'avg',
			header: 'Per ciclo',
			align: 'right',
			render: (row) => `${fmtNum(row.avgSharedKwh)} kWh`,
		},
		{
			key: 'cycles',
			header: 'Cicli/mese',
			align: 'right',
			variant: 'muted',
			render: (row) => fmtNum(row.cycles),
		},
		{
			key: 'monthly',
			header: 'Stima mese',
			align: 'right',
			variant: 'monoAccent',
			render: (row) => `${fmtNum(row.monthlySharedKwh)} kWh`,
		},
	];

	return (
		<PanelCard
			title="Raccomandazioni giornaliere"
			aside={
				<CardBadge tone={available ? 'purple' : 'orange'}>
					{available ? `${totals.members} SOCI` : 'ND'}
				</CardBadge>
			}
		>
			{loading ? (
				<StatusBlock kind="loading" size="sm" message="Calcolo del piano di oggi…" />
			) : !available || !selected ? (
				<StatusBlock
					kind="empty"
					size="sm"
					message={
						reason
							? REASON_MESSAGE[reason]
							: 'Nessuna azione utile nei prossimi giorni: non è previsto surplus da intercettare.'
					}
				/>
			) : (
				<>
					<Grid columns={compact ? 1 : 'responsive3'} gap="sm">
						<StatTile
							label="Condivisa in più oggi"
							value={`${fmtNum(totals.todayKwh)} kWh`}
							hint={date ? formatDay(date) : 'oggi'}
							tone="success"
						/>
						<StatTile
							label="Stima mensile"
							value={`${fmtNum(totals.monthlyKwh)} kWh`}
							hint={`proiezione su ${horizonDays} giorni di previsione`}
							tone="accent"
						/>
						<StatTile
							label="Energia intercettata"
							value={`${fmtNum(selected.today.totalSharedGainKwh)} kWh`}
							hint={`${selected.memberName}: surplus che diventa condiviso`}
						/>
					</Grid>

					{/* Con un socio solo (il socio che guarda il proprio piano) non c'è
					    nulla da scegliere. */}
					{members.length > 1 && (
						<LabeledSelect label="Socio" value={selected.memberId} onChange={setSelectedId}>
							{members.map((member) => (
								<option key={member.memberId} value={member.memberId}>
									{member.memberName} - {fmtNum(member.monthly.totalSharedGainKwh)} kWh/mese
								</option>
							))}
						</LabeledSelect>
					)}

					{/* Alert picco rete: la finestra in cui la CER compra tutto quello che
					    consuma, e i carichi che il piano ne toglie anticipandoli. */}
					{selected.today.peakAlert && selected.today.peakAlert.deficitKwh > 0 && (
						<IncentiveRow
							icon="⚠️"
							iconTone="warnSoft"
							name={`Picco di rete previsto ${windowLabel(
								selected.today.peakAlert.fromHour,
								selected.today.peakAlert.toHour,
							)}`}
							desc={
								selected.today.peakAlert.shiftedKwh > 0
									? `Il FV è già sceso e la CER preleva ${fmtNum(selected.today.peakAlert.deficitKwh)} kWh dalla rete. Anticipando i carichi qui sotto togli ${fmtNum(selected.today.peakAlert.shiftedKwh)} kWh da quella finestra.`
									: `Il FV è già sceso e la CER preleva ${fmtNum(selected.today.peakAlert.deficitKwh)} kWh dalla rete. Evita di avviare carichi pesanti in questa finestra.`
							}
							action={<CardBadge tone="orange">CONGESTIONE</CardBadge>}
						/>
					)}

					{selected.today.recommendations.length === 0 ? (
						<StatusBlock
							kind="empty"
							size="sm"
							message={`Oggi nessuna azione utile per ${selected.memberName}. La stima mensile tiene conto dei giorni in cui torna a esserlo.`}
						/>
					) : (
						selected.today.recommendations.map((rec) => (
							<IncentiveRow
								key={rec.appliance}
								icon={APPLIANCE_ICON[rec.appliance]}
								iconTone="accentSoft"
								name={`${rec.label} - avvia alle ${hourLabel(rec.startHour)}`}
								desc={
									<>
										{`Invece delle ${hourLabel(rec.baselineHour)} (${rec.baselineFascia} → ${rec.fascia}): il ciclo da ${fmtNum(rec.kwh)} kWh trova `}
										<Text as="strong" variant="inherit" tone="success">
											{`${fmtNum(rec.sharedGainKwh)} kWh`}
										</Text>
										{` di surplus, che diventano energia condivisa.`}
									</>
								}
								action={
									<CardBadge tone="green">{windowLabel(rec.startHour, rec.endHour)}</CardBadge>
								}
							/>
						))
					)}

					{!compact && selected.monthly.available && (
						<DataTable
							columns={monthlyColumns}
							rows={selected.monthly.items}
							rowKey={(row) => row.appliance}
						/>
					)}

					<AiBox>
						⚡ {selected.memberName}: {fmtNum(selected.monthly.totalSharedGainKwh)} kWh al mese di
						energia condivisa in più. La quota di surplus assegnata è il{' '}
						{Math.round(selected.surplusShare * 100)}% - il peso del socio sul consumo della CER
						nelle sole ore di sole.
					</AiBox>

					<Footnote>
						Lo spostamento non riduce i kWh prelevati: li porta nelle ore in cui la CER produce più
						di quanto consuma, dove diventano energia condivisa. Consumi e frequenze dei carichi
						sono valori di riferimento da etichetta energetica, non misure del singolo socio:
						l'anagrafica non registra quali apparecchi possiede.
					</Footnote>
				</>
			)}
		</PanelCard>
	);
}
DailyRecommendationsPanelView.displayName = 'DailyRecommendationsPanelView';

export { DailyRecommendationsPanelView };
